import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface TagData {
  id: number;
  tag_name: string;
  current_value: number;
  current_value_display: string;
  alarm_status: string;
  alarms_enabled: boolean;
  description: string;
  engineering_unit: string;
  overview_position_x: number | null;
  overview_position_y: number | null;
  link_to_area: string | null;
  area_id: number | null;
  area_name: string | null;
  display_mode?: 'status_icon' | 'minimal_text' | 'compact_button' | 'value_only' | 'badge' | 'gauge';
}

interface TagDisplayProps {
  tag: TagData;
  displayMode: 'status_icon' | 'minimal_text' | 'compact_button' | 'value_only' | 'badge' | 'gauge';
  showTooltips?: boolean;
  tooltipContent?: 'name_value' | 'name_description_value' | 'full_details';
  onClick?: (tag: TagData) => void;
  size?: 'small' | 'medium' | 'large';
}

const getTagStatusIcon = (status: string) => {
  switch (status) {
    case 'NORMAL':
    case 'ALARMS_DISABLED':
      return <CheckIcon sx={{ color: 'success.main', fontSize: '1rem' }} />;
    case 'HIGH_ALARM':
    case 'LOW_ALARM':
    case 'BOOLEAN_TRUE_ALARM':
    case 'BOOLEAN_FALSE_ALARM':
      return <WarningIcon sx={{ color: 'error.main', fontSize: '1rem' }} />;
    case 'COMMUNICATION_ERROR':
      return <CancelIcon sx={{ color: 'error.main', fontSize: '1rem' }} />;
    default:
      return <InfoIcon sx={{ color: 'info.main', fontSize: '1rem' }} />;
  }
};

const getAlarmColor = (status: string) => {
  switch (status) {
    case 'NORMAL':
    case 'ALARMS_DISABLED':
      return 'success.main';
    case 'HIGH_ALARM':
    case 'LOW_ALARM':
    case 'BOOLEAN_TRUE_ALARM':
    case 'BOOLEAN_FALSE_ALARM':
      return 'error.main';
    case 'COMMUNICATION_ERROR':
      return 'error.main';
    default:
      return 'info.main';
  }
};

const getTooltipContent = (tag: TagData, content: string) => {
  switch (content) {
    case 'name_value':
      return `${tag.tag_name}: ${tag.current_value_display}`;
    case 'name_description_value':
      return `${tag.tag_name} (${tag.description}): ${tag.current_value_display}`;
    case 'full_details':
      return `${tag.tag_name}\n${tag.description}\nValue: ${tag.current_value_display}\nStatus: ${tag.alarm_status}\nUnit: ${tag.engineering_unit}`;
    default:
      return `${tag.tag_name}: ${tag.current_value_display}`;
  }
};

const getGaugeValue = (tag: TagData) => {
  // For gauge display, we need to normalize the value
  // This is a simple implementation - you might want to store min/max values in the tag data
  const value = tag.current_value;
  if (typeof value === 'number') {
    // Assume a range of 0-100 for now, or use engineering unit hints
    if (tag.engineering_unit === '%') {
      return Math.min(100, Math.max(0, value));
    }
    // For other units, normalize to 0-100 based on typical ranges
    return Math.min(100, Math.max(0, (value / 100) * 100));
  }
  return 50; // Default to 50% for non-numeric values
};

export const TagDisplay: React.FC<TagDisplayProps> = ({
  tag,
  displayMode,
  showTooltips = true,
  tooltipContent = 'name_value',
  onClick,
  size = 'medium',
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick(tag);
    }
  };

  const renderContent = () => {
    switch (displayMode) {
      case 'status_icon':
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: size === 'small' ? 24 : size === 'large' ? 40 : 32,
              height: size === 'small' ? 24 : size === 'large' ? 40 : 32,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              border: '2px solid',
              borderColor: getAlarmColor(tag.alarm_status),
              cursor: onClick ? 'pointer' : 'default',
              '&:hover': onClick ? {
                bgcolor: 'action.hover',
                transform: 'scale(1.1)',
              } : {},
            }}
            onClick={handleClick}
          >
            {getTagStatusIcon(tag.alarm_status)}
          </Box>
        );

      case 'minimal_text':
        return (
          <Box
            sx={{
              cursor: onClick ? 'pointer' : 'default',
              '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
            }}
            onClick={handleClick}
          >
            <Typography
              variant={size === 'small' ? 'caption' : size === 'large' ? 'body1' : 'body2'}
              sx={{
                color: getAlarmColor(tag.alarm_status),
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              {tag.tag_name}
            </Typography>
            <Typography
              variant={size === 'small' ? 'caption' : size === 'large' ? 'body1' : 'body2'}
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {tag.current_value_display}
            </Typography>
          </Box>
        );

      case 'compact_button':
        return (
          <Card
            variant="outlined"
            sx={{
              cursor: onClick ? 'pointer' : 'default',
              borderColor: getAlarmColor(tag.alarm_status),
              '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
              minWidth: size === 'small' ? 120 : size === 'large' ? 200 : 150,
            }}
            onClick={handleClick}
          >
            <CardContent sx={{ p: size === 'small' ? 1 : 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {getTagStatusIcon(tag.alarm_status)}
                <Typography
                  variant={size === 'small' ? 'caption' : 'body2'}
                  sx={{ ml: 0.5, flexGrow: 1 }}
                >
                  {tag.tag_name}
                </Typography>
              </Box>
              <Typography
                variant={size === 'small' ? 'caption' : 'body2'}
                sx={{
                  color: getAlarmColor(tag.alarm_status),
                  fontWeight: 'bold',
                }}
              >
                {tag.current_value_display}
              </Typography>
            </CardContent>
          </Card>
        );

      case 'value_only':
        return (
          <Box
            sx={{
              cursor: onClick ? 'pointer' : 'default',
              '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
            }}
            onClick={handleClick}
          >
            <Typography
              variant={size === 'small' ? 'caption' : size === 'large' ? 'h6' : 'body1'}
              sx={{
                color: getAlarmColor(tag.alarm_status),
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              {tag.current_value_display}
            </Typography>
          </Box>
        );

      case 'badge':
        return (
          <Chip
            label={`${tag.tag_name}: ${tag.current_value_display}`}
            size={size === 'small' ? 'small' : 'medium'}
            sx={{
              bgcolor: getAlarmColor(tag.alarm_status),
              color: 'white',
              cursor: onClick ? 'pointer' : 'default',
              '&:hover': onClick ? { opacity: 0.8 } : {},
            }}
            onClick={handleClick}
          />
        );

      case 'gauge':
        const gaugeValue = getGaugeValue(tag);
        return (
          <Box
            sx={{
              cursor: onClick ? 'pointer' : 'default',
              '&:hover': onClick ? { bgcolor: 'action.hover' } : {},
              textAlign: 'center',
            }}
            onClick={handleClick}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={gaugeValue}
                size={size === 'small' ? 40 : size === 'large' ? 80 : 60}
                sx={{
                  color: getAlarmColor(tag.alarm_status),
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant={size === 'small' ? 'caption' : 'body2'}
                  component="div"
                  sx={{ fontWeight: 'bold' }}
                >
                  {tag.current_value_display}
                </Typography>
              </Box>
            </Box>
            <Typography
              variant={size === 'small' ? 'caption' : 'body2'}
              sx={{ mt: 0.5, color: 'text.secondary' }}
            >
              {tag.tag_name}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const content = renderContent();

  if (showTooltips) {
    return (
      <Tooltip
        title={getTooltipContent(tag, tooltipContent)}
        arrow
        placement="top"
      >
        <Box sx={{ display: 'inline-block' }}>
          {content}
        </Box>
      </Tooltip>
    );
  }

  return content;
}; 